/**
 * Pure formatters — a 1:1 port of the prototype's display helpers.
 *
 * Source: `design/Games Tonight Mobile.dc.html` lines 1446–1462 (`hueBg`,
 * `chipOf`, `fmt$`, `fmtD`, `fmtPk`, `ago`, `chkAgo`, `norm`, `revMeta`),
 * 1762 (`tagId`), 1883–1884 (`genPw`, `inviteFor`), 1994 (`ptFmt`),
 * 2003 (`ptPartyLbl`), 2441 (comment URL auto-linking).
 *
 * The only signature change: anything that read `Date.now()` internally takes
 * an explicit `now` with a `Date.now()` default, so the derivations are
 * testable without faking timers. Behaviour at the default is identical.
 */

import type { Game, GameId, Invite, User } from './types.ts';

/** Month abbreviations, prototype `fmtD` / `ptFmt`. */
export const MONTHS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
] as const;

/** `enrich()`'s month-abbrev → number map (prototype line 1654). */
export const MO: Record<string, string> = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

/** Lowercase alphanumerics only — the normalization every match uses. */
export function norm(s: string | null | undefined): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Filter-sheet tag id from a Steam tag display name (prototype line 1762). */
export function tagId(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** `TBA` / `FREE` / `$9.99` (prototype line 1451). */
export function fmt$(g: Pick<Game, 'up' | 'p'>): string {
  if (g.up || g.p === null || g.p === undefined) return 'TBA';
  if (g.p === 0) return 'FREE';
  return '$' + g.p.toFixed(2);
}

/** `OCT 2023` / `TBA` / `—` (prototype line 1452). */
export function fmtD(g: Pick<Game, 'rd' | 'up'>): string {
  if (!g.rd || g.rd === 'TBA') return g.up ? 'TBA' : '—';
  const p = g.rd.split('-');
  const mi = Number(p[1]) - 1;
  const mon = MONTHS[mi];
  if (!mon) return g.up ? 'TBA' : '—';
  return mon + ' ' + p[0];
}

/**
 * `23 OCT 2023` — the V2 rail's RELEASED row, falling back to `fmtD`'s
 * `OCT 2023` / `TBA` / `—` whenever Steam gave no day.
 *
 * `fmtD` is not modified and not bypassed: every absent-day path still goes
 * through it, so the `TBA`-when-upcoming and em-dash-when-unknown rules stay
 * stated once. This only adds a day when there provably is one.
 *
 * The day is NOT zero-padded (`3 OCT 2023`, not `03`) — that is the design's
 * form, and a leading zero reads like a code rather than a date.
 */
export function fmtDFull(g: Pick<Game, 'rd' | 'up' | 'rdFull'>): string {
  const p = (g.rdFull ?? '').split('-');
  const mon = MONTHS[Number(p[1]) - 1];
  // All three parts or none: a half-parsed value must fall back rather than
  // render `NaN` or a bare year.
  if (p.length !== 3 || !mon || !p[0] || !p[2]) return fmtD(g);
  return Number(p[2]) + ' ' + mon + ' ' + p[0];
}

/** `1.4M` / `240K` / `—` (prototype line 1453). */
export function fmtPk(n: number): string {
  if (!n) return '—';
  return n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1e3 ? Math.round(n / 1e3) + 'K' : '' + n;
}

/** `JUST NOW` / `NH AGO` / `ND AGO` / `NW AGO` (prototype line 1454). */
export function ago(ts: number, now: number = Date.now()): string {
  const h = (now - ts) / 36e5;
  if (h < 1) return 'JUST NOW';
  if (h < 24) return Math.round(h) + 'H AGO';
  const d = Math.round(h / 24);
  if (d < 14) return d + 'D AGO';
  return Math.round(d / 7) + 'W AGO';
}

/**
 * Freshness-chip age: `JUST NOW` (<8 s) / `NS AGO` / `NM AGO` / `NH AGO`
 * (prototype line 1455).
 *
 * `chkAt === null` reproduces the prototype's `this._chkAt || Date.now()`
 * fallback, which renders `JUST NOW` before the first check.
 */
export function chkAgo(chkAt: number | null, now: number = Date.now()): string {
  const s = Math.max(0, Math.round((now - (chkAt ?? now)) / 1000));
  if (s < 8) return 'JUST NOW';
  if (s < 60) return s + 'S AGO';
  const m = Math.round(s / 60);
  if (m < 60) return m + 'M AGO';
  return Math.round(m / 60) + 'H AGO';
}

/** `AUG 04`; `''` for a falsy timestamp (prototype line 1994). */
export function ptFmt(ms: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  return MONTHS[d.getMonth()] + ' ' + ('0' + d.getDate()).slice(-2);
}

/** `SOLO` / `1-4 CO-OP` / `1-6+ CO-OP` (prototype line 2003). */
export function ptPartyLbl(p: number): string {
  return p === 1 ? 'SOLO' : '1-' + p + (p === 6 ? '+' : '') + ' CO-OP';
}

export function hueBg(hue: number): string {
  return 'oklch(0.62 0.13 ' + hue + ')';
}

export interface Chip {
  ini: string;
  name: string;
  /** the chip's paint — the member's custom `col` when set, else the hue
   * formula. Named for the common case; every consumer treats it as "this
   * member's background". */
  hueBg: string;
  hasAv: boolean;
  avBg: string;
}

/**
 * Avatar chip for a user. A missing user is the deleted-member case and renders
 * as `FORMER MEMBER` — the prototype already had this fallback (line 1447, as
 * FORMER FRIEND), and ARCHITECTURE §3.1 makes it reachable for real (`added_by`
 * nulls on delete). It reads as `ADDED BY FORMER MEMBER` in an attribution, so
 * it stays a noun phrase rather than the party framing's `LEFT THE PARTY`.
 *
 * `col` wins over the hue formula (migration 3012): this is the single choke
 * point member color flows through, so the custom color reaches boards, chips,
 * attributions and rosters without any consumer changing. The initial's ink is
 * NOT here — `Avatar` derives it from the bg it is given (`lib/color.ts#inkOn`),
 * which also covers the hand-drawn initials that never go through `chipOf`.
 */
export function chipOf(u: User | null | undefined): Chip {
  if (!u) return { ini: '?', name: 'FORMER MEMBER', hueBg: 'var(--gt-border-2)', hasAv: false, avBg: '' };
  return {
    ini: (u.name[0] ?? '?').toUpperCase(),
    name: u.name,
    hueBg: u.col || hueBg(u.hue),
    hasAv: !!u.av,
    avBg: u.av ? 'url("' + u.av + '") center/cover no-repeat' : '',
  };
}

export interface RevMeta {
  /** Label and percentage as one string — the Detail hero line reads this. */
  t: string;
  c: string;
  /**
   * The two halves apart, for surfaces too narrow to hold `t`.
   *
   * A Discover tile is `nowrap` + `ellipsis` at 8.5px, and Steam's longest rung
   * is `OVERWHELMINGLY POSITIVE` — nearly twice the old `OVERWHELMING`. Rendered
   * as one string the ellipsis eats the RIGHT-hand end, which is the percentage:
   * the number, the only part that is a measurement, is the part that
   * disappears. Split, a tile can let the label truncate and pin the number.
   *
   * `l` is `''` where there is no rung to name (a percentage in the range no
   * rung can be derived from), and `pct` is `''` where there is no percentage —
   * so a row with a rung and no number renders `VERY POSITIVE`, not
   * `VERY POSITIVE 0%`.
   */
  l: string;
  pct: string;
  /**
   * Did Steam actually RATE this game — is `t` a score, or a stand-in?
   *
   * `false` for the three strings that occupy the review line without measuring
   * anything: `NO RATING DATA`, `WISHLIST TRENDING` and the bare `—`. A surface
   * that may only show a real rating asks this instead of reconstructing the
   * condition from `rp`/`rs`, and the reason it exists is that reconstructing it
   * went wrong twice in the same call site (`detailVM`'s stats band):
   *
   *   - `!!rp` let an UPCOMING game through, so a wishlist row with a
   *     percentage rendered `WISHLIST TRENDING` in a cell captioned
   *     `ALL TIME · STEAM` — a phrase that is not a score, presented as one.
   *   - after migration 3040 it started EXCLUDING a row Steam had rated,
   *     because a rung and a percentage arrive independently (`itemVolatile`
   *     lifts them behind separate gates). Such a row showed its rung on the
   *     review line and `NO REVIEW SCORE FROM STEAM YET` directly below it.
   *
   * Both are the same defect: the caller re-deriving a question this function
   * already answers. Keeping the answer here is what makes the two agree by
   * construction rather than by a comment asking the next person to remember.
   */
  scored: boolean;
}

/**
 * STEAM'S OWN LADDER, all nine rungs, in Steam's own words (2026-08-27, Marcus:
 * "make our review shape match steam's").
 *
 * What this replaced was a four-rung table keyed on a curated band with a
 * percentage-derived fallback — and the fallback was the problem. Steam's rungs
 * fold REVIEW COUNT into the label: Overwhelmingly Positive is 95%+ AND 500+
 * reviews, Very Positive is 80%+ AND 50+, and the separate `Positive` rung is
 * 80%+ on 10–49. Deriving a rung from a percentage alone cannot see that, so a
 * 96% game with twelve reviews rendered OVERWHELMING — a claim Steam itself
 * declines to make. `Game.rs` carries the rung Steam computed; this table only
 * names it.
 *
 * The negative rungs are new. The old ladder deliberately minted none and
 * showed a bare muted percentage below 40%, which was the honest thing to do
 * while the band was ours; it is not while the band is Steam's, and a filter
 * with a floor needs the bottom of the ladder to exist.
 */
const REV_RUNGS: Record<number, [string, string]> = {
  9: ['OVERWHELMINGLY POSITIVE ', 'var(--acc)'],
  8: ['VERY POSITIVE ', 'oklch(0.72 0.13 150)'],
  7: ['POSITIVE ', 'oklch(0.66 0.11 150)'],
  6: ['MOSTLY POSITIVE ', 'oklch(0.62 0.09 150)'],
  5: ['MIXED ', 'oklch(0.85 0.15 90)'],
  4: ['MOSTLY NEGATIVE ', 'oklch(0.78 0.14 55)'],
  3: ['NEGATIVE ', 'oklch(0.74 0.16 25)'],
  2: ['VERY NEGATIVE ', 'oklch(0.70 0.18 15)'],
  1: ['OVERWHELMINGLY NEGATIVE ', 'var(--acc2)'],
};

/** Every rung, worst first — the filter's ladder and the panel's copy. */
export const REV_RUNG_NAMES: ReadonlyArray<{ v: number; l: string }> = [
  { v: 1, l: 'OVERWHELMINGLY NEGATIVE' },
  { v: 2, l: 'VERY NEGATIVE' },
  { v: 3, l: 'NEGATIVE' },
  { v: 4, l: 'MOSTLY NEGATIVE' },
  { v: 5, l: 'MIXED' },
  { v: 6, l: 'MOSTLY POSITIVE' },
  { v: 7, l: 'POSITIVE' },
  { v: 8, l: 'VERY POSITIVE' },
  { v: 9, l: 'OVERWHELMINGLY POSITIVE' },
];

/**
 * The rung a PERCENTAGE alone can prove, for a row Steam has not rated for us
 * yet — a catalog row the nightly pool has not reached, mostly.
 *
 * It is deliberately partial, and the gaps are the point. Percentage is
 * decisive only in the middle of the ladder: 70–79 is Mostly Positive, 40–69 is
 * Mixed, 20–39 is Mostly Negative. Above 79 the answer is one of THREE rungs
 * and below 20 it is one of three others, and which one depends on a review
 * count this row does not carry. Returning 0 there is not a shortfall to be
 * patched later — it is the honest answer, and `revMeta` prints the bare
 * percentage rather than guessing between Positive and Overwhelmingly Positive.
 */
export function rungFromPct(rp: number): number {
  if (rp >= 80) return 0;
  if (rp >= 70) return 6;
  if (rp >= 40) return 5;
  if (rp >= 20) return 4;
  return 0;
}

/**
 * The rung to NAME a row with: Steam's if it answered, else the one the
 * percentage alone proves. `0` means no rung anyone can be sure of, and
 * `revMeta` prints the bare number there rather than guessing.
 */
export function rungOf(g: Pick<Game, 'rs' | 'rp'>): number {
  if (g.rs !== undefined && g.rs >= 1 && g.rs <= 9) return g.rs;
  return g.rp > 0 ? rungFromPct(g.rp) : 0;
}

/**
 * The HIGHEST rung a row could hold — what a floor has to compare against.
 *
 * A floor is a different question from a label, and conflating them is what
 * broke this twice in one afternoon. `rungOf` answers "which rung is this?" and
 * declines where the percentage cannot choose. A floor asks the weaker
 * question: "could this be at or above the bar?" — and that has an answer in
 * every case, because a percentage always bounds the rung from above even when
 * it cannot pin it.
 *
 * So exclusion happens only when the row is CERTAINLY below the bar, which is
 * decision 5 stated as a floor rather than as a label:
 *
 *   84% with no rung  → max 8. Passes VERY POSITIVE+ (8), fails OVERWHELMING
 *                       (9) — 84% cannot be Overwhelmingly Positive at any
 *                       review count, so hiding it is a fact, not a guess.
 *   60% with no rung  → max 5. Fails VERY POSITIVE+. This is the case the
 *                       screen was already calling MIXED while the filter let
 *                       it through.
 *   8%  with no rung  → max 3. Fails everything the panel offers. The previous
 *                       shape returned "unknown" here and let the worst rows on
 *                       the ladder through a MIXED+ floor that hid 30% rows.
 *   no percentage     → 9, i.e. passes every floor. Genuinely unknown.
 */
export function maxRungOf(g: Pick<Game, 'rs' | 'rp'>): number {
  if (g.rs !== undefined && g.rs >= 1 && g.rs <= 9) return g.rs;
  if (g.rp <= 0) return 9;
  if (g.rp >= 95) return 9;
  if (g.rp >= 80) return 8;
  if (g.rp >= 70) return 6;
  if (g.rp >= 40) return 5;
  if (g.rp >= 20) return 4;
  return 3;
}

/** Review line label + colour (prototype line 1457). */
/**
 * A score's colour (`GT_LOGIC.scClr`, DESIGN-CHANGELOG 2026-09-03 "Scores"):
 * ≥9 the accent, ≥7 Steam's POSITIVE green, ≥5 MIXED amber, else `--acc2`.
 * `null` — unscored — is the ghost tone the `—` badge wears.
 */
export function scoreClr(n: number | null | undefined): string {
  if (n === null || n === undefined) return 'var(--gt-ghost)';
  return n >= 9 ? 'var(--acc)' : n >= 7 ? 'var(--gt-rev-p)' : n >= 5 ? 'var(--gt-rev-mx)' : 'var(--acc2)';
}

export function revMeta(g: Pick<Game, 'live' | 'up' | 'rs' | 'rp'>): RevMeta {
  // `scored: false` on every `only()` string by construction — the three cases
  // that reach it are precisely the ones that name no measurement.
  const only = (t: string, c: string): RevMeta => ({ t, c, l: t, pct: '', scored: false });
  // The live short-circuit is only honest while Steam actually said nothing:
  // browse and live-search rows DO carry a real percentage (`toBrowseGame`
  // keeps `rp`), and 'NO RATING DATA' over a 97%-positive hit was a lie of
  // omission. A live row with a real `rp` rates like any other row.
  if (g.live && !g.rp && g.rs === undefined) return only('NO RATING DATA', 'var(--gt-muted-2)');
  if (g.up) return only('WISHLIST TRENDING', 'var(--acc2)');
  const m = REV_RUNGS[rungOf(g)];
  if (m) {
    // `pct` empty rather than `0%`: a browse row can carry Steam's rung with no
    // percentage beside it (`itemVolatile` lifts the two independently), and
    // `VERY POSITIVE 0%` states the opposite of the rung next to it.
    const pct = g.rp > 0 ? g.rp + '%' : '';
    const label = m[0].trimEnd();
    return { t: pct ? label + ' ' + pct : label, c: m[1], l: label, pct, scored: true };
  }
  // A percentage with no rung behind it: 80%+ or under 20% on a row Steam has
  // not rated for us, where the label would be a guess between three rungs. The
  // number is what we actually know.
  if (g.rp > 0) return { t: g.rp + '%', c: 'var(--gt-muted-2)', l: '', pct: g.rp + '%', scored: true };
  return only('—', 'var(--gt-muted-2)');
}

/** Two-letter fallback initials for cover art (prototype `tile`, line 1560). */
export function initialsOf(name: string): string {
  return (
    name
      .replace(/[^A-Za-z0-9 ]/g, '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('') || '?'
  ).toUpperCase();
}

/** Per-game fallback gradient (prototype `tile`, line 1561). */
export function fallGradient(id: GameId): string {
  const hue = (typeof id === 'number' ? id : 77) % 360;
  return 'linear-gradient(160deg, oklch(0.45 0.11 ' + hue + '), oklch(0.2 0.08 ' + hue + ') 78%)';
}

/** Playtest-card fallback gradient (prototype `ptArt`, line 1996). */
export function ptFallGradient(hue: number): string {
  return 'linear-gradient(160deg, oklch(0.5 0.13 ' + hue + '), oklch(0.2 0.1 ' + hue + ') 78%)';
}

/**
 * Steam category ids → the coarse label classes, for rows where the crew never
 * set a player count (`mx` 0 sentinel — every pool-minted row today).
 *
 * Id semantics verified 2026-08-10 against live `game_features.player_cat_ids`:
 * the only ids that occur in the table are exactly Steam's public category
 * enum, and spot checks agree with the store pages — Baldur's Gate 3 is
 * [2,1,9,38,48,27], Rocket League [2,1,49,36,37,9,38,39,24,27], Silksong and
 * Hades II are [2] alone, Among Us carries no 2 at all.
 *
 *   co-op  9 Co-op · 38 Online Co-op · 39 Local Co-op · 48 LAN Co-op
 *   multi  1 Multi-player · 20 MMO · 24 Shared/Split Screen ·
 *          27 Cross-Platform MP · 36 Online PvP · 37 Local PvP ·
 *          47 LAN PvP · 49 PvP
 *   solo   2 Single-player
 *
 * Co-op outranks multi because nearly every co-op row also carries the generic
 * multiplayer ids, and "can we play it together?" is the question the tile
 * answers. 24 (Shared/Split Screen) counts as multi, not co-op: Steam attaches
 * it to versus-only couch games too, so co-op would be a claim.
 */
const COOP_CATS = new Set([9, 38, 39, 48]);
const MULTI_CATS = new Set([1, 20, 24, 27, 36, 37, 47, 49]);

/**
 * Does Steam POSITIVELY say this game is single-player only?
 *
 * True on exactly one shape: the categories block names Single-player (2) and
 * NOTHING else. That is the one seat-shaped fact
 * `supported_player_categoryids` can state about a row whose `mx` is the
 * unknown sentinel, and it is what lets the crew lens act on a browse row at
 * all — "how many of us can play together" is unanswerable as a NUMBER there,
 * but "none of you can" is answerable as a fact.
 *
 * `every === 2` rather than "includes 2 and none of the multi ids above",
 * deliberately: the two are equivalent for every payload Steam sends today
 * (the field's whole vocabulary is player-mode ids), but if Steam mints a new
 * multiplayer category tomorrow, `[2, newId]` under the looser test would hide
 * a multiplayer game by our ignorance of the id. Under this one it passes —
 * the only rows hidden are the ones whose entire claim we understand.
 *
 * Absent or empty is false — not asked is not solo (decision 5, unknown never
 * excludes).
 */
export function soloOnlyCats(cats: readonly number[] | undefined): boolean {
  return !!cats && cats.length > 0 && cats.every((c) => c === 2);
}

/**
 * The OTHER direction of the same block: does Steam POSITIVELY name a mode that
 * needs somebody else?
 *
 * `soloOnlyCats` answers "nobody can join you" and this answers "somebody can",
 * and they are deliberately not each other's negation — between them sits the
 * row whose claim we do not understand whole (`[2, newId]`), which both refuse.
 * That is decision 5 twice over: the SOLO lens hides on this positive, the
 * together lens hides on that one, and a claim neither recognises hides nothing.
 *
 * Same two sets `playersTxt` reads for its `CO-OP` / `MULTI` labels, so a row
 * the SOLO filter drops is exactly a row the tile does not label `SOLO`.
 */
export function togetherCats(cats: readonly number[] | undefined): boolean {
  return !!cats && cats.some((c) => COOP_CATS.has(c) || MULTI_CATS.has(c));
}

/**
 * The largest count still worth naming exactly. 16 is a crew; past it the
 * number stops being one.
 *
 * Nothing external picks this — it is a product decision about what the line
 * means. Every source that can answer above it (IGDB's `online_max`, PCGW's
 * Players field) is reporting a LOBBY OR SERVER CAPACITY, and the question this
 * line answers is "how many of us can play together". `128P` answers a
 * different question in the voice of a precise one.
 */
const PLAYERS_EXACT_MAX = 16;

/**
 * What we render instead of a capacity.
 *
 * `MANY` keeps the register the unknown path already established — `SOLO` /
 * `CO-OP` / `MULTI`: short, uppercase, unit-less — so the players line has one
 * vocabulary rather than two, and it rides through `detailVM`'s `NP` →
 * `N PLAYERS` expansion untouched for exactly the reason those do (the pattern
 * wants digits).
 *
 * A numeric band (`16+P`) was the alternative and lost on honesty. `punb` is a
 * claim about the SOURCE — the winning raw value was a sentinel capacity — not
 * about the size, so it can band a row whose resolved max is small, and a
 * numeric label would then assert a floor the row does not support. A word
 * cannot be wrong that way. `16+P` also sits one character from the exact `16P`
 * for a genuinely different claim, which is a bad place to put a distinction.
 */
const PLAYERS_BAND = 'MANY';

/**
 * `2-8P` / `SOLO` / `4P` / `MANY` / `—` (prototype `tile`, line 1566) — plus
 * the coarse `CO-OP` / `MULTI` / `SOLO` classes derived from `pcats` when `mx`
 * is the unknown sentinel, so a pool-minted row stops rendering an em dash for
 * a game Steam plainly labels co-op. DISPLAY ONLY: `knownOf`/`passFlt` keep
 * reading the raw `mn`/`mx` sentinels, per DISCOVERY decision 5 (unknown never
 * excludes).
 *
 * The band arm is display-only in the same strict sense, and it is the half
 * that is easy to get wrong: a banded row is a row we DO have a count for, so
 * it is `known.players` and a `4+` crew lens still has to match it. Only the
 * digits are withheld, never the fact.
 *
 * Order matters. The unknown sentinel is tested first and its answer is
 * unchanged: `mx` 0 means nothing resolved, and a `punb` on a row with no count
 * would be a contradiction we should not dignify by rendering a band for it.
 */
export function playersTxt(g: Pick<Game, 'mn' | 'mx' | 'pcats' | 'punb'>): string {
  if (!g.mx) {
    const cats = g.pcats ?? [];
    if (cats.some((c) => COOP_CATS.has(c))) return 'CO-OP';
    if (cats.some((c) => MULTI_CATS.has(c))) return 'MULTI';
    if (cats.includes(2)) return 'SOLO';
    return '—';
  }
  if (g.punb || g.mx > PLAYERS_EXACT_MAX) return PLAYERS_BAND;
  if (g.mn === g.mx) return g.mx === 1 ? 'SOLO' : g.mx + 'P';
  return g.mn + '-' + g.mx + 'P';
}

/**
 * The V2 rail's PLAY TOGETHER figure (prototype line 3156) — the exact count,
 * deliberately unbanded, and with no trailing `P` because the label above it
 * already says what the number counts.
 *
 * This is the ONE surface that prints digits `playersTxt` withholds. W5 makes
 * this cell the click target for correcting the count, and a cell reading `MANY`
 * cannot show you the value you are about to fix; the design says the same at
 * `Games Tonight.dc.html:1766`.
 *
 * **`punb` is not the same case as a big number, and it keeps the band.**
 * `mx > 16` means the count is real and merely large, so the digits are true and
 * only `playersTxt`'s "answers a different question in the voice of a precise
 * one" argument withholds them — this cell overrides that argument. `punb` means
 * the winning raw value was a SENTINEL capacity: there is no measurement beneath
 * it to show or to correct, and `playersTxt` explains why a numeric label on
 * such a row asserts a floor the row does not support.
 *
 * The `!mx` fallback is `playersTxt`'s and not the design's bare `'—'`: a
 * pool-minted row carries no count but does carry `pcats`, and the app already
 * answers `CO-OP` / `MULTI` / `SOLO` from them. The count is moving into this
 * cell, so the cell inherits that obligation rather than regressing it.
 */
export function capNumTxt(g: Pick<Game, 'mn' | 'mx' | 'pcats' | 'punb'>): string {
  if (!g.mx) return playersTxt(g);
  if (g.punb) return PLAYERS_BAND;
  if (g.mx === 1) return 'SOLO';
  return g.mn === g.mx ? String(g.mx) : (g.mn || 1) + '-' + g.mx;
}

/**
 * The seat meter's caption (prototype line 3158), which is the whole point of
 * the cell: a raw `1-4` becomes `4 OF YOUR 6 AT ONCE`, read against the crew's
 * actual size.
 *
 * Both trees use these DESKTOP strings. The phone mirror ships a shortened set
 * and is the only one the 2026-08-13 export still renders, but `W.md` quotes the
 * desktop wording as the spec — picking the phone's copy merely because it
 * survived the export would silently shrink it.
 *
 * **`punb` refuses the whole sentence, and that is the half W4 shipped wrong.**
 * `capNumTxt` bands a sentinel row to `MANY`; this caption took the raw `mx` and
 * did arithmetic on it, so Factorio (`res_max` 65535, unclamped by design)
 * rendered `MANY` directly above `ALL 6 FIT · ROOM FOR 65529 MORE` — the same
 * sentinel the cell above had just refused to print, wearing a fact's clothes
 * and doing subtraction. Every arm of this function needs a real maximum: the
 * headroom clause subtracts from it, and the `N OF YOUR M` clause asserts a
 * ceiling. A sentinel supports neither, so it gets the same answer as no count
 * at all — which is also the honest invitation, because W5 makes this cell the
 * place you fix it. Answers the seat-meter half of `W.md` open question 5.
 */
export function fitTxt(mx: number, n: number, punb = false): string {
  if (!mx) return 'NO COUNT — SET ONE';
  if (punb) return 'NO RELIABLE COUNT — SET ONE';
  if (mx >= n) {
    return mx > n ? 'ALL ' + n + ' FIT · ROOM FOR ' + (mx - n) + ' MORE' : 'ALL ' + n + ' OF YOU FIT';
  }
  return Math.min(mx, n) + ' OF YOUR ' + n + ' AT ONCE';
}

/**
 * The player-count editor's baseline note: what we currently think the count is,
 * and who said so. `''` means print no note at all.
 *
 * **This function is the whole of conflict C3.** The design ships four `STEAM
 * SAYS …` strings, and Steam publishes category FLAGS and no numeric maximum —
 * 944 of 952 browsable rows carry `0/0` from it. Naming the wrong source is not
 * a cosmetic slip on a screen whose entire job is asking someone to correct a
 * number: it tells them the number came from somewhere it did not, so they
 * cannot judge whether it is worth correcting.
 *
 * **IGDB and PCGamingWiki publish a MAXIMUM ONLY**, and the resolver hardcodes
 * the minimum to 1 on both arms. So they are quoted a ceiling, never a range —
 * `IGDB SAYS UP TO 8`, never `IGDB SAYS 1-8`. Attributing that invented `1` to
 * them would be the identical error the `STEAM SAYS` family was retired for,
 * committed on the majority of the rows this note exists to serve.
 *
 * Two branches print no source name because none can honestly be given: an
 * override IS the current value and the rung beneath it never reaches the
 * client, and a `punb` row's max is a sentinel rather than a count.
 */
export function playersSrcTxt(g: Pick<Game, 'mn' | 'mx' | 'punb' | 'psrc'>): string {
  // The slot belongs to the attribution line in this state — there is no
  // baseline to offer, because the resolver does not send the losing rung.
  if (g.psrc === 'override') return '';
  if (!g.mx) return 'NO SOURCE HAS A COUNT FOR THIS';
  if (g.punb) return "THE SOURCE COUNT ISN'T A REAL LIMIT";
  switch (g.psrc) {
    case 'igdb':
      return 'IGDB SAYS UP TO ' + g.mx;
    case 'pcgw':
      return 'PCGAMINGWIKI SAYS UP TO ' + g.mx;
    case 'steam_solo':
      return 'STEAM LISTS THIS AS SINGLE-PLAYER';
    default:
      // `editorial`, and the absent case with a real count — a row with no
      // `game_players` row still falls through to the curated catalog number,
      // and to a member reading this line the two are one claim: we wrote it
      // down. Keying the no-source copy on absence alone would print
      // `NO SOURCE HAS A COUNT` beside a count.
      return 'OUR CATALOG SAYS ' + (g.mn && g.mn !== g.mx ? g.mn + '-' + g.mx : String(g.mx));
  }
}

/** `-42%` when discounted, else `''` (prototype `tile`, line 1538). */
export function discountTxt(g: Pick<Game, 'o' | 'p'>): string {
  return g.o && g.p && g.o > g.p ? '-' + Math.round((1 - g.p / g.o) * 100) + '%' : '';
}

export const INVITE_BASE = 'https://games-tonight.example/';

/**
 * The base URL a user-facing copied link should carry: this deployment's own
 * origin. `location` is absent in the node test environment, where the
 * production domain is the only honest constant — the same default the
 * `admin-users` edge function's `APP_BASE_URL` falls back to.
 *
 * Existed because the Crew screen's per-row COPY INVITE shipped `INVITE_BASE`
 * (the prototype's fictional domain, X.md C5) — a link that 404s for real
 * members. Deriving from the origin is right in every mode: quarterdown.com in
 * prod, localhost in `dev:local`, the preview host on a branch deploy.
 *
 * NOTHING CALLS IT SINCE 2026-08-21. COPY INVITE came off both MANAGE CREW
 * screens with the 3026 redesign, and invitation links are now built
 * server-side from `APP_BASE_URL` (`_shared/crew.ts#joinLink`), which
 * `admin-users` refuses to guess. Kept rather than deleted because "this
 * deployment's own origin" is the correct answer for any future copyable link
 * and the reasoning above is the part that would be re-derived wrongly.
 */
export function appBase(): string {
  return typeof location !== 'undefined' ? location.origin : 'https://quarterdown.com';
}

const PW_WORDS = ['MOSS', 'PIXEL', 'WARP', 'BLIP', 'NOVA', 'GRID', 'LOOT', 'CRT'] as const;

/**
 * `MOSS-1234` — 9 characters, which clears the 8-char floor of
 * PORTING-NOTES D-01 (prototype line 1883).
 */
export function genPw(rand: () => number = Math.random): string {
  const w = PW_WORDS[Math.floor(rand() * PW_WORDS.length)] ?? 'MOSS';
  return w + '-' + Math.floor(1000 + rand() * 9000);
}

/** Prototype line 1884. */
export function inviteFor(u: Pick<User, 'k' | 'name'>, pw: string): Invite {
  return {
    name: u.name.toUpperCase(),
    pw,
    link: INVITE_BASE + 'join#user=' + u.k + '&code=' + encodeURIComponent(pw),
    // The key was already here, in the link; the panel just had no way to say
    // it out loud.
    key: u.k,
  };
}

export interface CommentPart {
  key: string;
  isLink: boolean;
  isTxt: boolean;
  txt: string;
  href: string;
}

/**
 * Comment body → text/link runs. Link text drops `https://www.` and truncates
 * to 42 chars + `…` when the raw URL is longer than 50 (prototype line 2441).
 */
export function commentParts(body: string): CommentPart[] {
  return body
    .split(/(https?:\/\/[^\s]+)/g)
    .filter((x) => x)
    .map((p, pi) =>
      /^https?:\/\//.test(p)
        ? {
            key: 'p' + pi,
            isLink: true,
            isTxt: false,
            txt: p.replace(/^https?:\/\/(www\.)?/, '').slice(0, 42) + (p.length > 50 ? '…' : ''),
            href: p,
          }
        : { key: 'p' + pi, isLink: false, isTxt: true, txt: p, href: '' },
    );
}

/** `PC / PS5 · …` platform run for the detail screen (prototype line 2420). */
export function platTxt(plat: string): string {
  const P: Array<[string, string]> = [
    ['P', 'PC'],
    ['S', 'PS5'],
    ['X', 'XBOX'],
    ['N', 'SWITCH'],
  ];
  return P.filter((p) => (plat || 'P').includes(p[0]))
    .map((p) => p[1])
    .join(' / ');
}
